import { ComponentIds } from '@inter-ikea-core/core';
import {
  Appearance,
  CoreApi as CoreAPI,
  Product,
  getProductReferenceFor
} from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import usePrevious from 'hooks/usePrevious';
import { getLuminance } from 'polished';
import * as R from 'ramda';
import { useEffect } from 'react';
import styled from 'styled-components';
import { SpaceProps } from 'styled-system';
import ColorSwatch from 'ui/ColorSwatch/ColorSwatch';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

type ColorOptions = {
  [value: string]: boolean;
};

type ColorLabel = {
  [value: string]: string;
};

type ColorFilterProps = SpaceProps & {
  title: string;
  products: Product[];
  filterState: any;
  onFilter: (productIdList: string[], filterState: any) => void;
};

const mapAppearances = R.map(R.prop('appearances'));
const uniqueByColor = R.uniqBy(R.prop('color'));
const constructColorOptions = R.reduce((acc, r: Appearance) => {
  return {
    ...acc,
    [r.color]: false
  };
}, {});

const constructColorLabels = R.reduce((acc, r: Appearance) => {
  return {
    ...acc,
    [r.color]: r.name
  };
}, {});

const getColorLabels = R.pipe<Product[], any, any, any, any>(
  mapAppearances,
  R.flatten,
  uniqueByColor,
  constructColorLabels
);

const getColorOptions = R.pipe<Product[], any, any, any, any>(
  mapAppearances,
  R.flatten,
  uniqueByColor,
  constructColorOptions
);

export const getFrameColorsInScene = (coreApi: CoreAPI, products: Product[]) => {
  const frameEntities = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  if (!frameEntities || frameEntities.length === 0) return [];
  const appearances = frameEntities.map(entity => {
    const productRef = getProductReferenceFor(entity);
    const frame = products.find(p => p.coreProductId === productRef);
    return frame?.appearances;
  });
  return Array.from(new Set(R.flatten(appearances).map(item => item?.color)));
};

const ColorFilter = ({ products, filterState, onFilter }: ColorFilterProps) => {
  const colors: ColorOptions = getColorOptions(products);
  const colorLabels: ColorLabel = getColorLabels(products);
  const filterColors = R.merge(colors, filterState);
  const coreApi = useCore();
  const previousColors = usePrevious(colors);

  const onApplyFilter = (color: string) => {
    if (!previousColors) return;

    const newFilterState = { ...filterState, [color]: !filterState[color] };
    const filterApplied = Object.values(newFilterState).some(f => f);
    let filteredProducts = products;

    if (filterApplied) {
      filteredProducts = filteredProducts.filter(product => {
        return (
          R.intersection(
            product.appearances.map(a => a.color),
            Object.keys(newFilterState).filter(key => newFilterState[key])
          ).length > 0
        );
      });
    }
    const filteredProductsId = filteredProducts.map(p => p.coreProductId);
    onFilter(filteredProductsId, newFilterState);
  };

  type ColorOptionPair = [string, boolean];

  const swatches = R.pipe<ColorOptions, ColorOptionPair[], ColorOptionPair[]>(
    R.toPairs,
    R.sort((a, b) => getLuminance(b[0]) - getLuminance(a[0]))
  )(filterColors);

  const onLoad = () => {
    const colors = getFrameColorsInScene(coreApi, products);
    colors.forEach(color => {
      if (color !== undefined && filterState[color] === undefined) onApplyFilter(color);
    });
  };

  useEffect(() => {
    return coreApi.subscribe('load', onLoad);
  });

  return (
    <Wrapper>
      {swatches.map(([color, checked]) => (
        <ColorSwatch
          data-testid={`color-filter-option-${color}`}
          aria-pressed={checked}
          key={color}
          onClick={() => onApplyFilter(color)}
          color={color}
          label={colorLabels[color]}
          mb="s"
          mr="s"
        ></ColorSwatch>
      ))}
    </Wrapper>
  );
};

export default ColorFilter;
